import React, { useState, useEffect, useRef } from "react";
import { useGlobal } from "../Context/GlobalContext";
import { retrieveAllDogs } from "../api/retrieveAllDogs";
import { publicDogModel } from "../types/publicDogModel";
import PublicBrowseDogCard from "../components/PublicBrowseDogCard";
import MobilePublicBrowseDogCard from "../components/MobilePublicBrowseDogCard";
import up from "../images/up-arrow.svg";
import down from "../images/down-arrow.svg";
import { Link } from "react-router-dom";
import { retrievePaginatedDogs } from "../api/retrievePaginatedDogs";
import { retrieveAllDogLocations } from "../api/retrieveDogLocations";
import LoadingSpinner from "../components/Spinner";
import { sampleDog } from "../types/sampleDog";
import { PaginationButton } from "../components/PaginationButton";
import { sortDogsByUrgency } from "../utils/sortDogsByUrgency";
import LocationFilterAccordion from "../components/LocationFilterAccordion";

interface BrowseDogsProps {
  statusProp: "atRisk" | "inRescueOrFoster" | "adopted" | "allDogs";
}

const BrowseDogs: React.FC<BrowseDogsProps> = ({ statusProp }) => {
  const [locations, setLocations] = useState<location[]>([]);
  type location = {
    city: string;
    state: string;
  };
  const [filters, setFilters] = useState({
    locationFilters: [] as string[],
    browseDogsFilter: [] as string[],
    sexFilter: [] as string[],
    sizeFilters: [] as string[],
    ageFilters: [] as string[],
    statusFilters: [] as string[],
  });
  const sampleDogs = [sampleDog, sampleDog, sampleDog];
  const [isMobile, setIsMobile] = useState(false);
  const [loadingMoreDogs, setLoadingMoreDogs] = useState(false);
  const [totalCalls, setTotalCalls] = useState(0);
  const [searchInputValue, setSearchInputValue] = useState("");
  const {
    params,
    setParams,
    browseChange,
    allDogs,
    setAllDogs,
    fetchedParams,
    setFetchedParams,
    page,
    setPage,
    browseScroll,
    setBrowseScroll,
  } = useGlobal();

  const [dataExists, setDataExists] = useState(false);
  const previousState = useRef<{ page: number; params: any }>({
    page,
    params,
  });
  const [totalPages, setTotalPages] = useState(1);
  const browsePupsDesktopRef = useRef<HTMLHeadingElement>(null);
  const browsePupsMobileRef = useRef<HTMLHeadingElement>(null);

  // Keep track of whether we're doing the first render
  const isFirstMount = useRef(true);

  // Track if we're in the initial rendering cycle after navigation
  const isInitialNavigation = useRef(true);

  // Initialize search input value from params.search
  useEffect(() => {
    setSearchInputValue(params.search);
  }, [params.search]);

  // Handle search action
  const handleSearch = () => {
    setParams({ ...params, search: searchInputValue });
  };

  // This effect should ONLY run on first component mount
  useEffect(() => {
    // Explicitly set the mount flags
    isFirstMount.current = true;
    isInitialNavigation.current = true;

    // Reset the first mount flag after a short delay
    const mountTimer = setTimeout(() => {
      if (isFirstMount.current) {
        isFirstMount.current = false;
      }
    }, 300); // Short delay to ensure initial rendering is complete

    // Reset the navigation flag after a longer delay
    const navTimer = setTimeout(() => {
      if (isInitialNavigation.current) {
        isInitialNavigation.current = false;
      }
    }, 800); // Longer delay to ensure all effects have run

    return () => {
      clearTimeout(mountTimer);
      clearTimeout(navTimer);
      isFirstMount.current = true; // Reset for next mount
      isInitialNavigation.current = true; // Reset for next mount
    };
  }, []);

  // Block automatic page resets to page 1 after the component has mounted
  const setPageSafely = (newPage: number) => {
    // Block any attempt to set the page to 1 automatically ONLY during the initial grace period
    // After grace period, allow filter changes to reset page to 1 since they're likely user actions
    if (
      newPage === 1 &&
      page > 1 &&
      (isFirstMount.current || isInitialNavigation.current)
    ) {
      return;
    }

    // Allow the page change - it's either a legitimate user action or after grace period
    setPage(newPage);
  };

  // Handle initial data load when the component mounts
  useEffect(() => {
    const loadInitialData = async () => {
      // First load location data
      const response = await retrieveAllDogLocations();
      setLocationFilters(response);

      // Then load dogs for the current page
      fetchDogsForPage(page, params);
    };

    loadInitialData();
  }, []);

  // Update data when filters change
  useEffect(() => {
    // Skip during the first render cycle after mount
    if (isFirstMount.current) {
      return;
    }

    // Also skip during initial navigation (returning to the page)
    if (isInitialNavigation.current) {
      return;
    }

    // At this point, we're outside the grace period, so this is likely a legitimate user filter change
    // Reset to page 1 as expected for filter changes
    setPageSafely(1);
    fetchDogsForPage(1, params);
  }, [
    params.adoptable_status,
    params.location,
    params.age,
    params.sex,
    params.status,
    params.size,
    params.search,
  ]);

  // Handle explicit page changes from user interaction
  const handlePageChange = (newPage: number) => {
    // Scroll to top of browse section
    const scrollToRef =
      window.innerWidth >= 768 ? browsePupsDesktopRef : browsePupsMobileRef;
    scrollToRef.current?.scrollIntoView();

    // Update global context
    setPageSafely(newPage);

    // Fetch data for the new page
    fetchDogsForPage(newPage, params);
  };

  // Cache full, globally sorted results for current filters to ensure urgency-first ordering before pagination
  const fullResultsCache = useRef<{
    key: string;
    dogs: publicDogModel[];
    count: number;
  } | null>(null);

  const buildParamsKey = (p: any) =>
    JSON.stringify({
      search: p.search,
      location: p.location,
      adoptable_status: p.adoptable_status,
      status: p.status,
      sex: p.sex,
      size: p.size,
      age: p.age,
    });

  const fetchAllPagesSorted = async (paramsObj: any) => {
    const key = buildParamsKey(paramsObj);
    if (fullResultsCache.current && fullResultsCache.current.key === key) {
      return fullResultsCache.current;
    }

    let pageCursor = 1;
    let aggregated: publicDogModel[] = [];
    let totalCount = 0;
    // Fetch all pages for current filters, then globally sort by urgency
    // so that page 1 always shows the most urgent dogs across the entire result set
    // regardless of backend pagination order
    // Assumes backend page size of 12
    // eslint-disable-next-line no-constant-condition
    while (true) {
      const response = await retrievePaginatedDogs(pageCursor, paramsObj);
      aggregated = aggregated.concat(response.results as publicDogModel[]);
      totalCount = response.count ?? aggregated.length;
      if (!response.next) break;
      pageCursor += 1;
    }

    const sorted = sortDogsByUrgency(aggregated);
    const cached = { key, dogs: sorted, count: totalCount };
    fullResultsCache.current = cached;
    return cached;
  };

  // Improved data fetching: globally sort by urgency BEFORE paginating client-side
  const fetchDogsForPage = async (pageNum: number, paramsObj: any) => {
    setLoadingMoreDogs(true);

    try {
      const { dogs, count } = await fetchAllPagesSorted(paramsObj);
      const pageSize = 12;
      const startIdx = (pageNum - 1) * pageSize;
      const endIdx = startIdx + pageSize;
      setAllDogs(dogs.slice(startIdx, endIdx));
      setTotalPages(Math.max(1, Math.ceil((dogs.length || count) / pageSize)));
      setFetchedParams(paramsObj);
      setLoadingMoreDogs(false);
    } catch (error) {
      setLoadingMoreDogs(false);
      console.error("Failed to fetch dogs data:", error);
    }
  };

  // This replaces the previous retrieveDogs function
  const retrieveDogs = fetchDogsForPage;

  // Clean up component-specific state only on unmount
  useEffect(() => {
    return () => {
      // Clear only local component state
      setAllDogs([]);
      setTotalPages(1);

      // Do NOT touch the global page state!
      // setPage(1) should NEVER be called here
    };
  }, []);

  // Calculate visible page range for pagination
  const calculateVisiblePageRange = (
    currentPage: number,
    totalPages: number,
  ): number[] => {
    if (totalPages < 1) return [];
    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    const pages = new Set<number>();
    pages.add(1);
    pages.add(2);
    pages.add(totalPages - 1);
    pages.add(totalPages);

    for (let p = currentPage - 1; p <= currentPage + 1; p++) {
      if (p >= 1 && p <= totalPages) {
        pages.add(p);
      }
    }

    const sortedPages = Array.from(pages).sort((a, b) => a - b);
    const result: number[] = [];
    for (let i = 0; i < sortedPages.length; i++) {
      if (i > 0 && sortedPages[i] !== sortedPages[i - 1] + 1) {
        result.push(-1);
      }
      result.push(sortedPages[i]);
    }

    return result;
  };

  // Using page from context for visible range calculation
  const visiblePageRange = calculateVisiblePageRange(page, totalPages);

  // Initialize filters from global state
  useEffect(() => {
    setFilters({
      locationFilters: params.location,
      browseDogsFilter: params.adoptable_status.map(paramToFilter),
      sexFilter: params.sex.map(paramToFilter),
      sizeFilters: params.size.map(paramToFilter),
      ageFilters: params.age.map(paramToFilter),
      statusFilters: params.status.map(paramToFilter),
    });
  }, []);

  useEffect(() => {
    const state = {
      filters,
      browseScroll,
    };

    localStorage.setItem("browseDogsState", JSON.stringify(state));
  }, [filters, browseScroll]);

  useEffect(() => {
    const savedState = localStorage.getItem("browseDogsState");

    if (savedState) {
      const { filters: savedFilters, browseScroll: savedScroll } =
        JSON.parse(savedState);

      // Only set filters if we don't already have them
      if (filters.browseDogsFilter.length === 0) {
        setFilters(savedFilters || {});
      }
      setBrowseScroll(savedScroll || 0);

      // Restore scroll position
      setTimeout(() => {
        window.scrollTo(0, savedScroll || 0);
      }, 0);
    }
  }, []);

  useEffect(() => {
    // Restore scroll position
    setTimeout(() => {
      window.scrollTo(0, browseScroll);
    }, 0);
  }, []);

  const handleScroll = () => {
    setBrowseScroll(window.scrollY);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    handleResize();

    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  const arraysEqual = (arr1: string[], arr2: string[]) => {
    if (arr1.length !== arr2.length) return false;
    return arr1.every((value, index) => value === arr2[index]);
  };

  const checkHeaderFilterChange = () => {
    if (arraysEqual(params.adoptable_status, fetchedParams.adoptable_status)) {
      return false;
    }
    return true;
  };

  const setLocationFilters = (
    locations: { city: string | null; state: string | null }[],
  ) => {
    const uniqueLocations: { city: string; state: string }[] = [];
    locations.forEach((location) => {
      const { city, state } = location;
      if (city && state) {
        const locationExists = uniqueLocations.some(
          (loc) => loc.city === city && loc.state === state,
        );

        if (!locationExists) {
          uniqueLocations.push({ city, state });
        }
      }
    });
    setLocations(uniqueLocations);
  };

  const retrieveDataExists = async (page: number, params: any) => {
    try {
      setLoadingMoreDogs(true);
      const response = await retrievePaginatedDogs(page, params);
      if (!response.next) {
        setDataExists(false);
      } else {
        setDataExists(true);
      }
      setFetchedParams(params);
      setLoadingMoreDogs(false);
    } catch (error) {
      setLoadingMoreDogs(false);
      console.error("Failed to check if data exists:", error);
    }
  };

  useEffect(() => {
    const fetchDogData = async () => {
      // If we already have the data for the current page, skip fetching
      if (
        allDogs.length > 0 &&
        !checkHeaderFilterChange() &&
        previousState.current.page === page
      ) {
        window.scrollTo(0, browseScroll);
        return;
      }

      // Fetch new data
      if (allDogs.length > 0 && !checkHeaderFilterChange()) {
        retrieveDataExists(page, params);
      } else {
        const response = await retrieveAllDogLocations();
        setLocationFilters(response);
        retrieveDogs(page || 1, params);
      }

      // Update previous state after fetch
      previousState.current = { page, params };
    };

    // Don't reset filters on mount at all - we want to preserve the state
    // Only initialize if needed
    if (
      filters.browseDogsFilter.length === 0 &&
      filters.locationFilters.length === 0 &&
      filters.sexFilter.length === 0 &&
      filters.sizeFilters.length === 0 &&
      filters.ageFilters.length === 0 &&
      filters.statusFilters.length === 0
    ) {
      setFilters({
        locationFilters: [],
        browseDogsFilter: [],
        sexFilter: [],
        sizeFilters: [],
        ageFilters: [],
        statusFilters: [],
      });
    }

    // Don't call this - we're now using our new data loading approach
    // fetchDogData();
  }, []);

  // Update previous state whenever page or params change
  useEffect(() => {
    previousState.current = { page, params };
  }, [page, params]);

  const paramConversion = [
    { filter: "At-Risk", param: "at_risk" },
    { filter: "In Rescue/Foster Care", param: "in_rescue_or_foster" },
    { filter: "Browse All Dogs", param: "" },

    { filter: "Male", param: "male" },
    { filter: "Female", param: "female" },
    { filter: "<15lbs", param: "15lbs" },
    { filter: "16-30lbs", param: "16-30lbs" },
    { filter: "31-50lbs", param: "31-50lbs" },
    { filter: "51-80lbs", param: "51-80lbs" },
    { filter: ">80lbs", param: "80lbs" },

    { filter: "<6 months", param: "6 months" },
    { filter: "7 months - 3 years", param: "7 months - 3 years" },
    { filter: "4 - 6 years", param: "4 - 6 years" },
    { filter: ">6 years", param: "6 years" },
  ];

  const filterToParam = (filter: string) => {
    const conversion = paramConversion.find((item) => item.filter === filter);
    return conversion ? conversion.param : filter;
  };

  const paramToFilter = (param: string) => {
    const conversion = paramConversion.find((item) => item.param === param);
    return conversion ? conversion.filter : param;
  };

  // Set filters to saved params
  useEffect(() => {
    setFilters({
      locationFilters: params.location,
      browseDogsFilter: params.adoptable_status.map(paramToFilter),
      sexFilter: params.sex.map(paramToFilter),
      sizeFilters: params.size.map(paramToFilter),
      ageFilters: params.age.map(paramToFilter),
      statusFilters: params.status.map(paramToFilter),
    });
  }, [browseChange]);

  // set params to filters onchange of filters
  useEffect(() => {
    // Skip the first render to avoid resetting params on component mount
    if (isFirstMount.current) {
      isFirstMount.current = false;
      return;
    }

    setParams((prevParams) => ({
      ...prevParams,
      location: filters.locationFilters,
      adoptable_status: filters.browseDogsFilter.map(filterToParam),
      sex: filters.sexFilter.map(filterToParam),
      size: filters.sizeFilters.map(filterToParam),
      age: filters.ageFilters.map(filterToParam),
      status: filters.statusFilters.map(filterToParam),
    }));
    setTotalCalls((prev) => prev + 1);
  }, [filters]);


  const handleFilterCheckboxClick = (data: string, filterType: string) => {
    const tempFilters = { ...filters };
    switch (filterType) {
      case "Location":
        if (tempFilters.locationFilters.includes(data)) {
          tempFilters.locationFilters = tempFilters.locationFilters.filter(
            (location) => location !== data,
          );
        } else {
          tempFilters.locationFilters = [...tempFilters.locationFilters, data];
        }
        break;
      case "Browse Dogs":
        if (tempFilters.browseDogsFilter.includes(data)) {
          tempFilters.browseDogsFilter = tempFilters.browseDogsFilter.filter(
            (filter) => filter !== data,
          );
        } else {
          tempFilters.browseDogsFilter = [
            ...tempFilters.browseDogsFilter,
            data,
          ];
        }
        // Ensure "Browse All Dogs" is not included
        tempFilters.browseDogsFilter = tempFilters.browseDogsFilter.filter(
          (filter) => filter !== "Browse All Dogs",
        );
        // Ensure "Browse All Dogs" is selected if no other filters are active
        if (
          tempFilters.browseDogsFilter.length === 0 ||
          (tempFilters.browseDogsFilter.includes("At-Risk") &&
            tempFilters.browseDogsFilter.includes("In Rescue/Foster Care"))
        ) {
          tempFilters.browseDogsFilter = ["Browse All Dogs"];
        }

        break;
      case "Sex":
        if (tempFilters.sexFilter.includes(data)) {
          tempFilters.sexFilter = [];
        } else {
          tempFilters.sexFilter = [data];
        }
        break;

      case "Size":
        if (tempFilters.sizeFilters.includes(data)) {
          tempFilters.sizeFilters = tempFilters.sizeFilters.filter(
            (size) => size !== data,
          );
        } else {
          tempFilters.sizeFilters = [...tempFilters.sizeFilters, data];
        }
        break;
      case "Age":
        if (tempFilters.ageFilters.includes(data)) {
          tempFilters.ageFilters = tempFilters.ageFilters.filter(
            (age) => age !== data,
          );
        } else {
          tempFilters.ageFilters = [...tempFilters.ageFilters, data];
        }
        break;
      case "Status":
        if (tempFilters.statusFilters.includes(data)) {
          tempFilters.statusFilters = tempFilters.statusFilters.filter(
            (status) => status !== data,
          );
        } else {
          tempFilters.statusFilters = [...tempFilters.statusFilters, data];
        }
        break;
      default:
        break;
    }
    setFilters(tempFilters);
  };

  const filterMapping: { [key: string]: string[] } = {
    "Browse Dogs": filters.browseDogsFilter,
    Sex: filters.sexFilter,
    Size: filters.sizeFilters,
    Age: filters.ageFilters,
  };

  const getFilterArray = (sectionText: string): string[] => {
    return filterMapping[sectionText] || [];
  };


  const keys = [
    {
      text: "Red: Medical",
      filterText: "medical",
      color: "border-red-500",
      active_color: "bg-red-300",
    },
    {
      text: "Blue: Behavioral",
      filterText: "behavioral",
      color: "border-blue-500",
      active_color: "bg-blue-300",
    },
    {
      text: "Green: Adoptable",
      filterText: "adoptable",
      color: "border-green-500",
      active_color: "bg-green-300",
    },
  ];

  const filter = [
    {
      text: "Browse Dogs",
      content: ["At-Risk", "In Rescue/Foster Care"],
    },
    {
      text: "Sex",
      content: ["Male", "Female"],
    },
    {
      text: "Size",
      content: ["<15lbs", "16-30lbs", "31-50lbs", "51-80lbs", ">80lbs"],
    },
    {
      text: "Age",
      content: ["<6 months", "7 months - 3 years", "4 - 6 years", ">6 years"],
    },
  ];

  const [searchDropDownData, setSearchDropDownData] = useState({
    text: "Search By: ",
    content: [
      "At-Risk",
      "In Rescue / Foster Care",
      "Adopted",
      "Browse All Dogs",
    ],
    expanded: false,
  });
  const toggleSearchDropDown = () => {
    setSearchDropDownData((prevData) => ({
      ...prevData,
      expanded: !prevData.expanded,
    }));
  };

  const [filterDropDownData, setFilterDropDownData] = useState({
    text: "Filter By: ",
    expanded: false,
  });

  const toggleFilterDropDown = () => {
    setFilterDropDownData((prevData) => ({
      ...prevData,
      expanded: !prevData.expanded,
    }));
  };

  const [addCheckedStatusSearch, setAddCheckedStatusSearch] = useState({
    addAtRiskChecked: false,
    addInRescueChecked: false,
    addAdopted: false,
    addAll: false,
  });

  return (
    <section className="mb-20 font-poppins text-gray-dark">
      <section className="w-full bg-gray-light px-6 py-12 md:px-0">
        <div className="flex h-full flex-col items-center justify-center gap-6">
          <h1 className="text-center text-3xl font-bold text-blue">
            Find your Pup
          </h1>
          <h2 className="text-center max-w-5xl">
          Your new best friend is waiting! These pups from our partner rescues and shelters are looking for a home. Any dogs marked "Urgent" are still at the shelter and need help quickly. Can you be the hero they need by adopting, fostering, or donating?
          </h2>
          <div className="text-center max-w-5xl">
            <div className="text-left">
              <ol className="list-decimal list-inside space-y-2">
                <li>Filter by your location and other desired criteria</li>
                <li>View a dog's profile to learn more about them</li>
                <li>Follow profile links/instructions for adopting, fostering, or donating</li>
              </ol>
            </div>
          </div>
        </div>
      </section>
      <div className="mx-auto my-20 max-w-[1500px] gap-10 px-6 md:px-3">
        <div className="hidden md:flex">
          <>
            <div className="flex flex-col items-center">
              <div className="flex w-full items-center justify-center"></div>
              <section className="flex w-full flex-row">
                <div className="mb-6 mr-10 mt-6 w-[300px] text-left">
                  <button className="mt-5 w-full text-nowrap rounded-md bg-blue py-2 font-semibold text-white shadow-md hover:bg-blue-dark">
                    <Link to="/adopted" className="">
                      Adopted Dogs
                    </Link>
                  </button>
                  <div className="mb-6 mt-5">
                    <h1 className="text-xl font-bold text-blue">Search by: </h1>
                    <h2 className="mt-2 text-lg font-bold">
                      ID #, Name or Breed:
                    </h2>
                    <input
                      className="mt-1 w-full bg-gray4 px-3 py-2 shadow-box"
                      id="email"
                      type="email"
                      value={searchInputValue}
                      onChange={(e) => setSearchInputValue(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          handleSearch();
                        }
                      }}
                    />
                    <div className="mt-4 flex w-full justify-center">
                      <button
                        onClick={handleSearch}
                        className="w-full text-nowrap rounded-md bg-blue py-2 font-semibold text-white shadow-md hover:bg-blue-dark"
                      >
                        Search
                      </button>
                    </div>
                  </div>
                  <LocationFilterAccordion
                    locations={locations}
                    selected={filters.locationFilters}
                    onToggle={(location) =>
                      handleFilterCheckboxClick(location, "Location")
                    }
                  />
                  <div>
                    {filter.map((section, index) => (
                      <div key={index} className="p-2">
                        <h2 className="border-b-gray-dark text-lg font-bold">
                          {section.text}:
                        </h2>
                        <div className="ml-6 p-2">
                          {section.content.map((item, index) => (
                            <div key={index} className="flex items-center">
                              <input
                                type="checkbox"
                                className="mr-2"
                                onChange={() => {
                                  handleFilterCheckboxClick(item, section.text);
                                }}
                                checked={getFilterArray(section.text).includes(
                                  item,
                                )}
                              />
                              <label htmlFor={`checkbox-${index}`}>
                                {item}
                              </label>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex w-full flex-col items-center">
                  <h1
                    ref={browsePupsDesktopRef}
                    id="browse-all-dogs"
                    className="mb-10 text-center text-3xl font-bold text-blue"
                  >
                    Browse Dogs
                    {(() => {
                      const active = filters.browseDogsFilter.filter(
                        (f) => f !== "Browse All Dogs",
                      );
                      if (active.length === 1) {
                        return <span>&nbsp;|&nbsp;{active[0]}</span>;
                      }
                      return null;
                    })()}
                  </h1>
                  {/* Desktop tabs removed; use sidebar filters instead */}
                  {allDogs && allDogs.length > 0 && (
                    <>
                      <div className="grid w-full grid-cols-2 gap-6 xl:grid-cols-3">
                        <>
                          {allDogs.map((dog: publicDogModel) => (
                            <PublicBrowseDogCard
                              key={dog.dog_id + dog.name}
                              dog={dog}
                            />
                          ))}
                        </>
                      </div>
                    </>
                  )}

                  {!allDogs ||
                    (allDogs.length === 0 && (
                      <div className="invisible grid w-full grid-cols-2 gap-6 xl:grid-cols-3">
                        <>
                          {sampleDogs.map((dog: publicDogModel) => (
                            <PublicBrowseDogCard
                              key={dog.dog_id + dog.name}
                              dog={dog}
                            />
                          ))}
                        </>
                      </div>
                    ))}

                  {totalPages > 0 && (
                    <div className="mt-12 flex items-center justify-center gap-2">
                      <PaginationButton
                        type="left"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                      />

                      {visiblePageRange.map((pageNum, index) => {
                        if (pageNum === -1) {
                          return (
                            <PaginationButton
                              key={`ellipsis-${index}`}
                              type="text"
                              text="..."
                              disabled={true}
                              onClick={() => {}}
                            />
                          );
                        }
                        return (
                          <PaginationButton
                            key={`page-${pageNum}`}
                            type="text"
                            text={pageNum.toString()}
                            active={pageNum === page}
                            onClick={() => handlePageChange(pageNum)}
                          />
                        );
                      })}

                      <PaginationButton
                        type="right"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                      />
                    </div>
                  )}
                  {loadingMoreDogs && (
                    <div>
                      <div className="inset-0 z-50 flex h-750 items-center justify-center bg-white bg-opacity-75">
                        <div className="flex flex-col items-center">
                          <div className="loader mb-4 h-32 w-32 rounded-full border-8 border-t-8 border-blue ease-linear"></div>
                          <h2 className="text-2xl font-semibold text-blue">
                            Loading...
                          </h2>
                          <p className="text-blue">
                            Please wait while we fetch these pups.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </section>
            </div>
          </>
        </div>
        <div className="flex justify-center md:hidden">
          <>
            <section className="flex flex-col items-center">
              <h1
                ref={browsePupsMobileRef}
                id="browse-all-dogs"
                className="text-center text-xl font-bold text-blue"
              >
                {"Browse Dogs"}
                {(() => {
                  const active = filters.browseDogsFilter.filter(
                    (f) => f !== "Browse All Dogs",
                  );
                  if (active.length === 1) {
                    return <> {"|"} {active[0]}</>;
                  }
                  return null;
                })()}
              </h1>

              <section className="flex flex-row">
                <div className="flex w-full flex-col items-center justify-center">
                  <div className="flex w-[300px] flex-col text-left">
                    <button
                      className="flex w-full flex-row items-center justify-between border border-gray-300 px-4 py-2 text-left text-lg font-bold"
                      onClick={() => toggleSearchDropDown()}
                    >
                      {searchDropDownData.text}
                      {searchDropDownData.expanded ? (
                        <img src={up} alt="Up Arrow" className="h-4 w-4" />
                      ) : (
                        <img src={down} alt="Down Arrow" className="h-4 w-4" />
                      )}
                    </button>
                    {searchDropDownData.expanded && (
                      <div className="w-full border border-t-0 border-gray-300 p-4">
                        <div className="mb-6">
                          <h2 className="mt-2 text-lg font-bold">
                            ID #, Name or Breed:
                          </h2>
                          <input
                            className="mt-1 w-full bg-gray4 px-3 py-2 shadow-box"
                            id="email"
                            type="email"
                            value={searchInputValue}
                            onChange={(e) => setSearchInputValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                handleSearch();
                              }
                            }}
                          />
                        </div>
                        <div className="mb-2 mt-2 flex w-full justify-center">
                          <button
                            onClick={handleSearch}
                            className="w-full text-nowrap rounded-md bg-blue py-2 font-semibold text-white shadow-md hover:bg-blue-dark"
                          >
                            Search
                          </button>
                        </div>

                        <div className="mb-2 flex flex-col gap-1">
                          <button className="mt-5 w-full text-nowrap rounded-md bg-blue py-2 font-semibold text-white shadow-md hover:bg-blue-dark">
                            <Link to="/adopted" className="text-sm font-bold">
                              Adopted Dogs
                            </Link>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="mb-6 mt-6 w-[300px] text-left">
                    <button
                      className="flex w-full flex-row items-center justify-between border border-gray-300 px-4 py-2 text-left text-lg font-bold"
                      onClick={() => toggleFilterDropDown()}
                    >
                      {filterDropDownData.text}
                      {filterDropDownData.expanded ? (
                        <img src={up} alt="Up Arrow" className="h-4 w-4" />
                      ) : (
                        <img src={down} alt="Down Arrow" className="h-4 w-4" />
                      )}
                    </button>
                    {filterDropDownData.expanded && (
                      <div className="mb-6 mr-10 w-[300px] border border-t-0 border-gray-300 text-left">
                        <LocationFilterAccordion
                          locations={locations}
                          selected={filters.locationFilters}
                          onToggle={(location) =>
                            handleFilterCheckboxClick(location, "Location")
                          }
                        />
                        <div>
                          {filter.map((section, index) => (
                            <div key={index} className="p-2">
                              <h2 className="border-b-gray-dark text-lg font-bold">
                                {section.text}:
                              </h2>
                              <div className="ml-6 p-2">
                                {section.content.map((item, index) => (
                                  <div
                                    key={item + index}
                                    className="flex items-center"
                                  >
                                    <input
                                      type="checkbox"
                                      className="mr-2"
                                      onChange={() => {
                                        handleFilterCheckboxClick(
                                          item,
                                          section.text,
                                        );
                                      }}
                                      checked={getFilterArray(
                                        section.text,
                                      ).includes(item)}
                                    />
                                    <label htmlFor={`checkbox-${index}`}>
                                      {item}
                                    </label>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                    {allDogs.map((dog: publicDogModel, index) => (
                      <MobilePublicBrowseDogCard
                        key={`${dog.dog_id}-${dog.name}-${index}`}
                        dog={dog}
                      />
                    ))}
                  </div>
                  {totalPages > 0 && (
                    <div className="mt-12 flex items-center justify-center gap-2">
                      <PaginationButton
                        type="left"
                        onClick={() => handlePageChange(page - 1)}
                        disabled={page === 1}
                      />

                      {visiblePageRange.map((pageNum, index) => {
                        if (pageNum === -1) {
                          return (
                            <PaginationButton
                              key={`ellipsis-${index}`}
                              type="text"
                              text="..."
                              disabled={true}
                              onClick={() => {}}
                            />
                          );
                        }
                        return (
                          <PaginationButton
                            key={`page-${pageNum}`}
                            type="text"
                            text={pageNum.toString()}
                            active={pageNum === page}
                            onClick={() => handlePageChange(pageNum)}
                          />
                        );
                      })}

                      <PaginationButton
                        type="right"
                        onClick={() => handlePageChange(page + 1)}
                        disabled={page === totalPages}
                      />
                    </div>
                  )}
                </div>
              </section>
            </section>
          </>
          <div />
        </div>
      </div>
    </section>
  );
};

export default BrowseDogs;
